import { useDispatch } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { setDeviceId } from 'js/utils/pas.util';
import { useDeviceSelector } from 'js/store/hooks';

import { updateDeviceAction } from 'js/hooks/device/store/actions';

export const useDevicePublicAPI = () => {
    const dispatch = useDispatch();
    const device = useDeviceSelector();

    const setId = useCallback((deviceId) => {
        dispatch(updateDeviceAction({ deviceId }));
        setDeviceId(deviceId);
    }, [dispatch]);

    return useMemo(() => {
        const {
            alias,
            browserLanguage,
            browserName,
            browserVersion,
            clientPlatform,
            deliveryPlatform,
            deviceId,
            formFactor,
            isDesktop,
            isInWrapper,
            isLandscape,
            isMobile,
            isOtherBrowser,
            isOtherOs,
            isPortrait,
            isTablet,
            isTouchDevice,
            name,
            orientation,
            os,
            osVersion,
            ua,
            ...otherDeviceProps
        } = device;

        return {
            /**
             * Permanently set new Device ID.
             * @memberOf Playtech.API.device
             * @param id {String}
             * @function
             * @example
             * window.Playtech.API.device.setId('e8ff9bed-6d5c-42f1-babc-7a7fec93fb36');
             */
            setId,
            /**
             * Returns currently Device ID.
             * Device ID is stored in local storage.
             * @memberOf Playtech.API.device
             * @function
             * @returns {String}
             * @example
             * // 'e8ff9bed-6d5c-42f1-babc-7a7fec93fb36'
             * window.Playtech.API.device.getId();
             */
            getId: () => deviceId,
            /**
             * Assigned device alias.
             * @memberOf Playtech.API.device
             * @example
             * // 'other'
             * window.Playtech.API.device.alias;
             * @type {String}
             */
            alias,
            /**
             * Detected browser language.
             * @memberOf Playtech.API.device
             * @example
             * // 'ru-RU'
             * window.Playtech.API.device.browserLanguage;
             * @type {String}
             */
            browserLanguage,
            /**
             * Detected browser name.
             * @memberOf Playtech.API.device
             * @example
             * // 'chrome'
             * window.Playtech.API.device.browserName;
             * @type {String}
             */
            browserName,
            /**
             * Detected browser version.
             * @memberOf Playtech.API.device
             * @example
             * // '102'
             * window.Playtech.API.device.browserVersion;
             * @type {String}
             */
            browserVersion,
            /**
             * Client platform.
             * @memberOf Playtech.API.device
             * @example
             * // 'web'
             * window.Playtech.API.device.clientPlatform;
             * @type {String}
             */
            clientPlatform,
            /**
             * Delivery platform.
             * @memberOf Playtech.API.device
             * @example
             * // 'Hub2'
             * window.Playtech.API.device.deliveryPlatform;
             * @type {String}
             */
            deliveryPlatform,
            /**
             * Device ID.
             * @memberOf Playtech.API.device
             * @example
             * // '7914165b-9d1e-46fc-ae09-f1e04f438a27'
             * window.Playtech.API.device.deviceId;
             * @type {String}
             */
            deviceId,
            /**
             * One of 'mobile', 'tablet' or 'desktop'.
             * @memberOf Playtech.API.device
             * @example
             * // 'desktop'
             * window.Playtech.API.device.formFactor;
             * @type {String}
             */
            formFactor,
            /**
             * Whether device is desktop.
             * @memberOf Playtech.API.device
             * @type {Boolean}
             *  @example
             * // true
             * window.Playtech.API.device.isDesktop;
             */
            isDesktop,
            /**
             * Whether portal was opened in wrapper.
             * @memberOf Playtech.API.device
             * @type {Boolean}
             * @example
             * // true
             * window.Playtech.API.device.isInWrapper;
             */
            isInWrapper,
            /**
             * Whether device is in landscape.
             * @memberOf Playtech.API.device
             * @type {Boolean}
             * @example
             * // false
             * window.Playtech.API.device.isLandscape;
             */
            isLandscape,
            /**
             * Whether device is mobile.
             * @memberOf Playtech.API.device
             * @type {Boolean}
             * @example
             * // false
             * window.Playtech.API.device.isMobile;
             */
            isMobile,
            /**
             * Whether browser is not 'chrome', 'edge', 'firefox' nor 'safari'.
             * @memberOf Playtech.API.device
             * @type {Boolean}
             * @example
             * // false
             * window.Playtech.API.device.isOtherBrowser;
             */
            isOtherBrowser,
            /**
             * Whether os is not 'android', 'ios', 'windows' nor 'macintosh'.
             * @memberOf Playtech.API.device
             * @type {Boolean}
             * @example
             * // false
             * window.Playtech.API.device.isOtherOs;
             */
            isOtherOs,
            /**
             * Whether device is in portrait mode.
             * @memberOf Playtech.API.device
             * @type {Boolean}
             * @example
             * // false
             * window.Playtech.API.device.isPortrait;
             */
            isPortrait,
            /**
             * Whether device is tablet.
             * @memberOf Playtech.API.device
             * @type {Boolean}
             * @example
             * // false
             * window.Playtech.API.device.isTablet;
             */
            isTablet,
            /**
             * Whether device supports touch-handlers.
             * @memberOf Playtech.API.device
             * @type {Boolean}
             * @example
             * // false
             * window.Playtech.API.device.isTouchDevice;
             */
            isTouchDevice,
            /**
             * Detected device name.
             * @memberOf Playtech.API.device
             * @example
             * // 'windows'
             * window.Playtech.API.device.name;
             * @type {String}
             */
            name,
            /**
             * Device orientation. Could be portrait or landscape.
             * @memberOf Playtech.API.device
             * @type {String}
             * @example
             * // 'portrait'
             * window.Playtech.API.device.orientation;
             */
            orientation,
            /**
             * Detected device OS.
             * @memberOf Playtech.API.device
             * @example
             * // 'windows'
             * window.Playtech.API.device.os;
             * @type {String}
             */
            os,
            /**
             * Detected device OS version.
             * @memberOf Playtech.API.device
             * @example
             * // 11
             * window.Playtech.API.device.osVersion;
             * @type {String|Number}
             */
            osVersion,
            /**
             * Browser's user agent string.
             * @memberOf Playtech.API.device
             * @example
             * // 'mozilla/5.0 (windows nt 10.0; win64; x64) applewebkit/537.36 (khtml, like gecko) chrome/102.0.0.0 safari/537.36'
             * window.Playtech.API.device.ua;
             * @type {String}
             */
            ua,
            /**
             * Properties that doesn't described.
             * @memberOf Playtech.API.device
             * @type *|undefined
             */
            ...otherDeviceProps
        };
    }, [setId, device]);
};
